<script lang="ts">
  import { DebtorAccessLevel, type PermitGrant } from "$apis/parkito2";
  import { PermitGrantModel, PermitModel } from "$shared/models/mod.ts";
  import { Form, Validate } from "$shared/lib/validation_next.ts";
  import api, { fromApi } from "$shared/lib/api-parkito2.svelte.ts";
  import {
    BodyText,
    CardInline,
    CustomButton,
    ListNavigationItem,
    ModalDialog,
    Toast,
    Tooltip,
    ValCheckbox,
    ValInputField,
  } from "./mod.ts";
  import { Hor } from "../layouts/mod.ts";
  import { session } from "$shared/lib/session-parkito.ts";
  type Props = {
    permit: PermitModel;
    reloadData: () => void;
    accessLevel: DebtorAccessLevel;
    dpoAdmin?: boolean;
  };
  const { permit, accessLevel, reloadData, dpoAdmin }: Props = $props();
  const showCapacity = $derived(permit.capacity < permit.grantsCount());

  const usersOwnGrants = $derived(
    dpoAdmin
      ? permit.grants
      : permit.grants.filter((g) => g.data.user.id === $session?.user?.id),
  );
  const sharedGrants = $derived(
    permit.grants.filter((g) => g.data.user.id !== $session?.user?.id),
  );

  const licensePlateField = Form.field(
    Validate.len(2, undefined, "Måste minst vara 2 tecken lång"),
  );
  const renameGrantId = Form.input<string>(
    Validate.required("Tillstånd måste anges"),
  );
  const renameLicensePlateForm = Form.object({
    licensePlate: licensePlateField,
    grantId: renameGrantId,
  });

  const licensePlateAdder = fromApi((
    permitId: string,
    licensePlate: string,
  ) =>
    api.permitCreateGrant({
      path: { permit_id: permitId },
      body: { forSelf: { license_plate: licensePlate } },
    })
  );
  let showAddLicensePlateDialog = $state(false);
  const addLicensePlate = async () => {
    if ($licensePlateField.res.isOk()) {
      const resp = await $licensePlateAdder.load(
        permit.id,
        $licensePlateField.res.value,
      );
      if (resp.data) {
        Toast.success("Regnummer tillagd");
        reloadData();
        showAddLicensePlateDialog = false;
        licensePlateField.input.set("");
      } else {
        Toast.error("Kunda inte lägga till regnummer");
      }
    } else {
      Toast.error("Fel i formuläret");
    }
  };

  let showRenameLicensePlateDialog = $state(false);
  const openRenameLicensePlateDialog = async (grant: PermitGrant) => {
    licensePlateField.input.set(grant.data.licensePlate ?? "");
    renameGrantId.setValue(grant.id);
    showRenameLicensePlateDialog = true;
  };
  let licensePlateRenamer = fromApi((
    licensePlate: string,
    grantId: string,
  ) =>
    api.updateGrantLicensePlate({
      path: {
        grant_id: grantId,
      },
      body: {
        licensePlate,
      },
    })
  );

  const handleLicensePlateRename = async () => {
    if ($renameLicensePlateForm.res.isOk()) {
      const { licensePlate, grantId } = $renameLicensePlateForm.res.value;
      const res = await $licensePlateRenamer.load(licensePlate, grantId);
      if (res.data) {
        showRenameLicensePlateDialog = false;
        licensePlateField.input.set("");
        renameGrantId.setError("inget tillstånd valt");
      } else {
        Toast.error("Lyckades inte uppdatera");
      }
    } else {
      Toast.error("Fel i formulär");
    }

    reloadData();
  };

  const toggleGrantSession = async (grant: PermitGrantModel) => {
    let res = await grant.toggleSession();
    if (res.error) {
      Toast.error("Något gick fel");
      return;
    }

    if (grant.activeSession) {
      Toast.info("Parkering avaktiverad");
    } else {
      Toast.success("Parkering aktiverad");
    }
    reloadData();
  };

  const grantRemover = fromApi((grantId: string) =>
    api.permitBulkDeleteGrant({ body: { grantIds: [grantId] } })
  );
  const removeGrant = async (grant: PermitGrant) => {
    const res = await $grantRemover.load(grant.id);
    if (res.data) {
      reloadData();
    } else {
      Toast.error("Kunde inte ta bort regnummer");
    }
  };

  const emailField = Form.field(Validate.required().and(Validate.email()));
  const adminCheckbox = Form.checkbox(Validate.boolean());
  const form = Form.object({
    email: emailField,
    isAdmin: adminCheckbox,
  });

  let showInviteUserDialog = $state(false);
  const inviteSender = fromApi((
    permitId: string,
    email: string,
    isAdmin: boolean,
  ) =>
    api.permitCreateGrant({
      path: { permit_id: permitId },
      body: isAdmin
        ? { inviteAdmin: { email } }
        : { inviteUser: { email } },
    })
  );
  const sendInvite = async () => {
    if ($form.res.isOk()) {
      const resp = await $inviteSender.load(
        permit.id,
        $form.res.value.email,
        $form.res.value.isAdmin,
      );
      if (resp.data) {
        Toast.success("Inbjudan skickad");
        showInviteUserDialog = false;
        reloadData();
      } else {
        Toast.error("Inbjudan misslyckades");
      }
    } else {
      Toast.error("Fel i formuläret");
    }
  };
</script>

{#snippet GrantListItem(
  grant: PermitGrantModel,
  isFirst: boolean,
  isLast: boolean,
  isShared: boolean,
)}
  {@const   canActivate = (accessLevel === "Admin" || accessLevel === "Owner" ||
    grant.data.user.id === $session?.user?.id) &&
    permit.capacityLeft() > 0 && !!grant.data.licensePlate}
  {@const   canDeactivate = accessLevel === "Admin" || accessLevel === "Owner" ||
    grant.data.user.id === $session?.user?.id}
  {@const   statusType = grant.isActive() && canDeactivate
    ? "success"
    : (!grant.isActive()) && canActivate
    ? "error"
    : "disabled"}
  <ListNavigationItem
    onTitleClick={() => openRenameLicensePlateDialog(grant)}
    description={isShared ? grant.data.user.email ?? undefined : undefined}
    title={grant.data.licensePlate?.toUpperCase() ??
      "[Tryck här för att välja regnummer]"}
    iconSrc={accessLevel === "Owner" ? "/icons/cross.svg" : undefined}
    onIconClick={() => removeGrant(grant)}
    labelRight={grant.activeSession ? "Aktiv" : "Ej aktiv"}
    labelRightType={statusType}
    onLabelRightClick={statusType !== "disabled" ? () => toggleGrantSession(grant) : undefined}
    textRight={isShared && grant.data.accessLevel !== "User"
      ? grant.translatedAccessLevel()
      : undefined}
    {isFirst}
    {isLast}
  />
{/snippet}

<CardInline
  title={dpoAdmin
    ? "Regnummer"
    : showCapacity
    ? `Dina regnummer (${permit.activeSessionCount()}/${permit.capacity})`
    : "Dina regnummer"}
  buttonTitle={accessLevel === "Owner" || accessLevel === "Admin"
    ? "Lägg till"
    : undefined}
  buttonClick={() => showAddLicensePlateDialog = true}
>
  <BodyText size="s">
    Här kan du aktivera, avaktivera, döpa om och lägga till regnummer som ska
    användas för detta tillstånd.
  </BodyText>
  {#if showCapacity}
    <BodyText size="s">
      Du kan ha <strong>max {permit.capacity} fordon</strong> aktiva. Just nu
      har du <strong>{permit.activeSessionCount()} aktiva</strong>. Du har
      <strong>{permit.capacityLeft()} fordon kvar</strong> att aktivera.
    </BodyText>
  {/if}
  <div>
    {#each usersOwnGrants as grant, i}
      {@const isFirst = i === 0}
      {@const isLast = i === usersOwnGrants.length - 1}
      {@render GrantListItem(grant, isFirst, isLast, false)}
    {/each}
  </div>
</CardInline>

{#if showAddLicensePlateDialog}
  <ModalDialog
    title="Lägg till regnummer"
    onBackdropPressed={() => showAddLicensePlateDialog = false}
  >
    <ValInputField
      reserveErrorMsgHeight
      field={licensePlateField}
      placeholder="ABC123"
      type="text"
      label="Regnummer"
    />
    <Hor gap="m">
      <CustomButton
        type="ghost"
        onClick={() => showAddLicensePlateDialog = false}
      >
        Avbryt
      </CustomButton>
      <CustomButton
        loading={$licensePlateAdder.isLoading()}
        disabled={!$licensePlateField.res.isOk()}
        onClick={addLicensePlate}
      >
        Lägg till
      </CustomButton>
    </Hor>
  </ModalDialog>
{/if}

{#if showRenameLicensePlateDialog}
  <ModalDialog
    title="Ändra regnummer"
    onBackdropPressed={() => showRenameLicensePlateDialog = false}
  >
    <ValInputField
      reserveErrorMsgHeight
      field={licensePlateField}
      placeholder="ABC123"
      type="text"
      label="Regnummer"
    />
    <Hor gap="m">
      <CustomButton
        type="ghost"
        onClick={() => showRenameLicensePlateDialog = false}
      >
        Avbryt
      </CustomButton>
      <CustomButton
        loading={$licensePlateRenamer.isLoading()}
        disabled={$renameLicensePlateForm.res.isErr()}
        onClick={handleLicensePlateRename}
      >
        Uppdatera
      </CustomButton>
    </Hor>
  </ModalDialog>
{/if}

{#if !dpoAdmin}
  <CardInline
    title="Delade tillstånd"
    buttonTitle={accessLevel === "Owner" ? "Lägg till" : undefined}
    buttonClick={() => showInviteUserDialog = true}
  >
    <img
      src="/graphics/keys.svg"
      alt="parkando-cars"
      class="mx-auto pt-[8px] pb-[32px]"
    />
    <BodyText>
      Här kan du dela ut ditt tillstånd till andra användare.
    </BodyText>
    <div>
      {#each sharedGrants as grant, i}
        {@const isFirst = i === 0}
        {@const isLast = i === sharedGrants.length - 1}
        {@render GrantListItem(grant, isFirst, isLast, true)}
      {/each}
    </div>
  </CardInline>
{/if}

{#if showInviteUserDialog}
  <ModalDialog
    title="Dela ut tillstånd"
    onBackdropPressed={() => showInviteUserDialog = false}
  >
    <ValInputField
      reserveErrorMsgHeight
      field={emailField}
      type="text"
      label="E-post"
    />
    <Hor>
      <ValCheckbox
        field={adminCheckbox}
        label="Admin"
      />
      <Tooltip
        text="Om du ger denna användare admin-rättigheter så
        kommer den kunna aktivera och avaktivera andra användares
        parkering på detta tillstånd."
      />
    </Hor>
    <Hor gap="m">
      <CustomButton type="ghost" onClick={() => showInviteUserDialog = false}>
        Avbryt
      </CustomButton>
      <CustomButton
        loading={$inviteSender.isLoading()}
        disabled={!$emailField.res.isOk()}
        onClick={sendInvite}
      >
        Skicka inbjudan
      </CustomButton>
    </Hor>
  </ModalDialog>
{/if}
